// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI XR Studio — the panel where a journalist makes a video of the globe and
 * orders it (docs/AYNI_XR_STUDIO_TECH_DESIGN.md §3, §7; plan items J3, J6;
 * the stops, moves and effects of docs/AYNI_XR_STUDIO_MOTION_PLAN.md).
 *
 * - **Output**: a title, the video's shape, resolution, frame rate and file
 *   (MP4, H.265, ProRes 4444, GIF or a PNG still; transparent space for ProRes
 *   and PNG — plan J7). Its length comes from the stops.
 * - **Stops**: the video is a Khipu played on a timeline; each stop is one of
 *   its steps (a place, a date and what the globe shows), made the way
 *   teachers make Khipus: **Add the view as a stop**, a template, or **Open
 *   one of my Khipus** (opened as a new video, the original untouched). A
 *   timeline strip shows each stop's arrival and hold (drag a hold's end to
 *   change it); the selected stop sets its arrival (camera move, feel, data
 *   change), what the camera does while it holds, and its effects
 *   (spotlight, depth blur, vignette, grain, title card, callouts, arrows,
 *   value counter). Effects never change the data inside the focus.
 * - **Playing is recorded** (owner, 2026-10-11): while the panel is open,
 *   each time the data plays, the date it started from becomes a stop (unless
 *   the selected one is already there) and the date it stopped on another,
 *   reached by running through the dates for as long as it played
 *   (`playbackTake.ts`); a pause before the next play becomes that stop's
 *   hold. So the video and its Khipu show what was played.
 * - **Finishing**: motion blur (twice the credits), an end card with every
 *   credit, an animated brand, reframing for other shapes.
 * - **Preview**: the stops played live on the globe, moves and effects as the
 *   video will have them; the video itself is rendered on a server.
 * - **Order**: "Save and get a quote" keeps the video as a Khipu in the
 *   account (`/api/khipus`) and asks for a quote (`/api/studio/jobs`);
 *   "Order the video" confirms it (credits held); "Your videos" lists the
 *   orders, with a download link for 30 days once delivered, and "Keep" to
 *   keep one until the licence's term ends (1 credit), its poster and
 *   **Embed code** for the outlet's own site (plan J11).
 * - **Team** (Newsroom, plan J12): editors switch on approval and name other
 *   editors; with approval on, a member's order waits in "Your videos" until
 *   an editor approves or declines it.
 *
 * Typing never redraws the panel (a redraw would take the next field or
 * button away mid-gesture); adding, removing or choosing a stop does.
 * The draft lives as long as the page, so closing the panel loses nothing.
 * Shown from the bar's Studio button on the plans that include render
 * credits (Media Desk, Newsroom).
 */

import { t } from '../i18n'
import { escapeAttr, escapeHtml } from './domUtils'
import { listKeptKhipus, readKeptKhipu, saveKhipu, type KeptKhipu } from '../services/khipuStore'
import { newKhipuId, type Khipu, type KhipuEffects, type KhipuStep, type KhipuTransition } from '../services/khipu'
import { emptyStory, insertStep, moveStep, removeStep, sceneToStep, setText, stepFromGlobe, updateStep } from '../services/khipuEdit'
import type { SoloScene } from '../services/soloRecorder'
import {
  ALPHA_FORMATS, GIF_MAX_SECONDS, NO_FINISH, quoteShot, SHOT_ASPECTS, SHOT_FORMATS, SHOT_FRAME_RATES, SHOT_RESOLUTIONS,
  type Shot, type ShotAspect, type ShotFinish, type ShotFormat, type ShotOutput, type ShotResolution, type ShotView,
} from '../services/studio/shot'
import { buildFilmKhipu } from '../services/studio/shotKhipu'
import { autoSeconds, planFilm, type FilmPlan } from '../services/studio/film'
import { SHOT_TEMPLATES, templateStops, type ShotTemplate } from '../services/studio/templates'
import { checkBrand, DEFAULT_ACCENT, BRAND_LOGO_MAX_CHARS, type BrandContent } from '../services/studio/brandKit'
import { embedSnippet } from '../services/studio/embed'
import { MEASUREMENT_COLORS } from '../services/measure/colors'
import { createPlaybackWatch, MIN_HOLD, runPieces, type PlaybackEvent } from '../services/studio/playbackTake'

export interface StudioHost {
  datasetId(): string | null
  datasetTitle(): string
  /** The main globe's camera now. */
  view(): ShotView | null
  /** The data date on screen now (epoch ms). */
  dateMs(): number | null
  /** Whether the dataset is playing now (recorded as stops while the panel is open). */
  playing(): boolean
  /** The dataset's first frame and the end of its axis (exclusive), or null without dates. */
  dateRange(): { startMs: number; endMs: number } | null
  setView(view: ShotView): void
  seekToMs(ms: number): void
  /** A date as the date track names it (the data's precision). */
  formatDate(ms: number): string
  announce(message: string): void
  /** What the globe shows, as Record a Khipu takes it (dataset, camera, date, overlays, pins). */
  scene(): SoloScene
  titleOf(datasetId: string): string | null
  layerTitleOf(layerId: string): string | null
  /** Put a stop on the globe, as the Khipu editor's Show on the globe does. */
  showStep(k: Khipu, index: number): Promise<void>
  /** Play the stops live on the globe (the preview); `stop` ends it, `onEnd` says it ended. */
  previewFilm(k: Khipu, film: FilmPlan, onEnd: () => void): { stop(): void }
  language(): string
}

interface BrandKitSummary extends BrandContent {
  id: string
  name: string
}

interface JobSummary {
  id: string
  output: ShotOutput
  quote: number
  status: 'quoted' | 'rendering' | 'delivered' | 'failed' | 'expired'
  createdAt: string
  /** When a delivered file is deleted. */
  expiresAt: string | null
}

interface TeamSummary {
  team: boolean
  approvalRequired: boolean
  editor: boolean
  members?: Array<{ accountId: string; email: string; editor: boolean }>
}

const PANEL_ID = 'studio-panel'
const ASPECT_KEY: Record<ShotAspect, 'studio.aspect.wide' | 'studio.aspect.tall' | 'studio.aspect.square' | 'studio.aspect.portrait'> = {
  '16:9': 'studio.aspect.wide', '9:16': 'studio.aspect.tall', '1:1': 'studio.aspect.square', '4:5': 'studio.aspect.portrait',
}
const RESOLUTION_LABEL: Record<ShotResolution, string> = { '1080p': '1080p (HD)', '4k': '4K', '8k': '8K (Newsroom)' }
const ERROR_KEY: Record<string, 'studio.error.notConfigured' | 'studio.error.insufficientCredits' | 'studio.error.planRequired' | 'studio.error.unauthorized' | 'studio.error.newsroomOnly' | 'studio.error.editorOnly' | 'studio.error.lastEditor' | 'studio.error.voiceMissing' | 'studio.voice.needRights' | 'studio.voice.badAudio' | 'studio.voice.tooLarge'> = {
  voice_missing: 'studio.error.voiceMissing',
  rights_required: 'studio.voice.needRights',
  bad_audio: 'studio.voice.badAudio',
  too_large: 'studio.voice.tooLarge',
  editor_only: 'studio.error.editorOnly',
  last_editor: 'studio.error.lastEditor',
  newsroom_only: 'studio.error.newsroomOnly',
  not_configured: 'studio.error.notConfigured',
  insufficient_credits: 'studio.error.insufficientCredits',
  plan_required: 'studio.error.planRequired',
  unauthorized: 'studio.error.unauthorized',
  signed_out: 'studio.error.unauthorized',
}
const POLL_MS = 15_000
const MOVES = ['fly', 'glide', 'cut', 'path'] as const
const FEELS = ['gentle', 'snappy', 'linear'] as const
const DURINGS = ['still', 'orbit', 'pushIn', 'pullOut'] as const
const DATA_MOVES = ['cut', 'crossfade', 'wipe', 'timeRun', 'basemap'] as const
const NEW_STOP_HOLD = 4

interface Draft {
  title: string
  output: ShotOutput
  /** The stops: a Khipu whose steps they are; null until the first stop. */
  khipu: Khipu | null
  brand: ({ id: string } & BrandContent) | null
  finish: ShotFinish
}

const freshDraft = (): Draft => ({
  title: '',
  output: { aspect: '16:9', resolution: '4k', fps: 30, seconds: 0, format: 'mp4', transparent: false, stillAt: 0 },
  khipu: null,
  brand: null,
  finish: { ...NO_FINISH, animatedBrand: true, reframe: true },
})

let draft: Draft = freshDraft()
let selected = 0
let quote: JobSummary | null = null
let kits: BrandKitSummary[] = []
let mine: KeptKhipu[] = []
let newKitOpen = false
let newKitLogo: string | null = null

let panel: HTMLElement | null = null
let host: StudioHost | null = null
let trigger: HTMLElement | null = null
let preview: { stop(): void } | null = null
const playWatch = createPlaybackWatch()
let playWatchTimer = 0
/** The stop the last recorded run ended on: the pause before the next play becomes its hold. */
let runEndStep: string | null = null
const PLAY_WATCH_MS = 150
/** The voice-over uploaded in this session, to hear with the preview (the server keeps the file for the render). */
let voicePlayer: HTMLAudioElement | null = null
let voiceBusy = false
let pollTimer = 0

/** The price line; a video with stops on several globes says they are priced per globe (owner, 2026-10-10). */
function studioPriceText(shot: Shot): string {
  const text = t('studio.price', { credits: quoteShot(shot) })
  return shot.film?.stops.some((stop) => stop.globes > 1) ? `${text} ${t('studio.price.perGlobe')}` : text
}

export function isStudioOpen(): boolean {
  return panel !== null
}

const errorText = (code: unknown, fallback: 'studio.error.save' | 'studio.error.generic' = 'studio.error.generic'): string =>
  t(typeof code === 'string' && ERROR_KEY[code] ? ERROR_KEY[code] : fallback)

function say(message: string): void {
  const status = panel?.querySelector<HTMLElement>('.studio-status')
  if (status) status.textContent = message
  host?.announce(message)
}

const option = (value: string, label: string, selectedNow: boolean) =>
  `<option value="${escapeAttr(value)}" ${selectedNow ? 'selected' : ''}>${escapeHtml(label)}</option>`

/** The shot the draft makes now, or null while it is not one. */
function built(): { khipu: Record<string, unknown>; shot: Shot } | null {
  if (!draft.khipu) return null
  return buildFilmKhipu({ title: draft.title, khipu: draft.khipu, output: draft.output, brand: draft.brand, finish: draft.finish }, new Date().toISOString())
}

const steps = (): KhipuStep[] => draft.khipu?.path.steps ?? []
const step = (): KhipuStep | null => steps()[selected] ?? null
const lang = () => host?.language() ?? 'en'
const textOf = (v: Record<string, string> | undefined) => (v ? v[lang()] ?? v.en ?? Object.values(v)[0] ?? '' : '')

/** A short name for a stop: its title, else where it is. */
function stopName(s: KhipuStep, i: number): string {
  const title = textOf(s.title as Record<string, string> | undefined)
  if (title) return title
  if (s.view) return `${s.view.lat.toFixed(1)}°, ${s.view.lon.toFixed(1)}°`
  return t('studio.stop.n', { n: i + 1 })
}

function timelineHtml(film: FilmPlan | null): string {
  if (!film) return ''
  const total = film.seconds || 1
  return `<div class="studio-timeline" role="list" aria-label="${escapeAttr(t('studio.stops.timeline'))}">${film.stops.map((s) => {
    const i = steps().findIndex((st) => st.id === s.stepId)
    const arrive = s.holdStart - s.start
    return `<div class="studio-tl-stop ${i === selected ? 'is-selected' : ''}" role="listitem" data-index="${i}" style="flex-grow:${((s.end - s.start) / total).toFixed(4)}">
      ${arrive > 0 ? `<span class="studio-tl-arrive" style="flex-grow:${arrive.toFixed(3)}"></span>` : ''}
      <button type="button" class="studio-tl-hold" style="flex-grow:${s.hold.toFixed(3)}" data-index="${i}" aria-pressed="${i === selected}">${escapeHtml(String(i + 1))}</button>
      <span class="studio-tl-handle" data-index="${i}" aria-hidden="true"></span>
    </div>`
  }).join('')}</div>`
}

function stopRows(): string {
  if (!steps().length) return `<p class="studio-empty">${escapeHtml(t('studio.stops.empty'))}</p>`
  return `<ol class="studio-list studio-stops">${steps().map((s, i) => `
    <li class="studio-stop-row ${i === selected ? 'is-selected' : ''}" data-index="${i}">
      <button type="button" class="studio-stop-pick" aria-pressed="${i === selected}">${escapeHtml(`${i + 1}. ${stopName(s, i)}`)}</button>
      <span class="studio-stop-hold-label">${escapeHtml(`${s.hold ?? NEW_STOP_HOLD} s`)}</span>
      <button type="button" class="studio-stop-up" aria-label="${escapeAttr(t('studio.stop.up'))}" ${i === 0 ? 'disabled' : ''}>↑</button>
      <button type="button" class="studio-stop-down" aria-label="${escapeAttr(t('studio.stop.down'))}" ${i === steps().length - 1 ? 'disabled' : ''}>↓</button>
      <button type="button" class="studio-stop-remove" ${steps().length <= 1 ? 'disabled' : ''}>${escapeHtml(t('studio.stop.remove'))}</button>
    </li>`).join('')}</ol>`
}

/** The selected stop's form: its place, hold, arrival, hold motion, data change and effects. */
function stopEditor(film: FilmPlan | null): string {
  const s = step()
  if (!s) return ''
  const tr: KhipuTransition = s.transition ?? {}
  const fx: KhipuEffects = s.effects ?? {}
  const first = selected === 0
  const filmStop = film?.stops.find((fs) => fs.stepId === s.id)
  const prev = film && filmStop && filmStop.index > 0 ? film.stops[filmStop.index - 1] : null
  const auto = prev && filmStop ? autoSeconds(tr.move ?? 'fly', tr.data ?? 'cut', prev.view, filmStop.view, tr.path ?? []).toFixed(1) : ''
  const num = (cls: string, value: number | undefined, min: number, max: number, stepSize: number, placeholder = '') =>
    `<input type="number" class="${cls}" min="${min}" max="${max}" step="${stepSize}" value="${value ?? ''}" ${placeholder ? `placeholder="${escapeAttr(placeholder)}"` : ''}>`
  const range = (cls: string, value: number | undefined) => `<input type="range" class="${cls}" min="0" max="1" step="0.05" value="${value ?? 0}">`
  const check = (cls: string, on: boolean | undefined, label: string) => `<label class="studio-check"><input type="checkbox" class="${cls}" ${on ? 'checked' : ''}> ${escapeHtml(label)}</label>`
  const field = (label: string, control: string, narrow = false) => `<label class="studio-field ${narrow ? 'studio-field-narrow' : ''}">${escapeHtml(label)} ${control}</label>`
  const callouts = (fx.callouts ?? []).map((c, i) => `
    <li class="studio-row" data-c="${i}">
      ${field(t('studio.fx.calloutLabel'), `<input type="text" class="fx-callout-label" maxlength="80" value="${escapeAttr(c.label)}">`)}
      ${check('fx-callout-value', c.value, t('studio.fx.calloutValue'))}
      <button type="button" class="fx-callout-remove">${escapeHtml(t('studio.stop.remove'))}</button>
    </li>`).join('')
  const arrows = (fx.arrows ?? []).map((a, i) => `
    <li class="studio-row" data-a="${i}">
      ${field(t('studio.fx.arrowLabel'), `<input type="text" class="fx-arrow-label" maxlength="80" value="${escapeAttr(a.label ?? '')}">`)}
      ${field(t('studio.fx.arrowColor'), `<select class="fx-arrow-color">${MEASUREMENT_COLORS.map((_, ci) => option(String(ci), t('studio.fx.colour', { n: ci + 1 }), (a.color ?? 0) === ci)).join('')}</select>`, true)}
      <span class="studio-row-summary">${escapeHtml(t('studio.fx.points', { n: a.points.length }))}</span>
      <button type="button" class="fx-arrow-point">${escapeHtml(t('studio.fx.arrowPoint'))}</button>
      <button type="button" class="fx-arrow-remove">${escapeHtml(t('studio.stop.remove'))}</button>
    </li>`).join('')
  return `
    <div class="studio-stop-edit" data-step="${escapeAttr(s.id)}">
      <h4 class="studio-subtitle">${escapeHtml(t('studio.stop.n', { n: selected + 1 }))}</h4>
      ${field(t('studio.stop.title'), `<input type="text" class="stop-title" maxlength="120" value="${escapeAttr(textOf(s.title as Record<string, string> | undefined))}">`)}
      <div class="studio-actions">
        <button type="button" class="stop-show">${escapeHtml(t('studio.stop.show'))}</button>
        <button type="button" class="stop-use">${escapeHtml(t('studio.stop.use'))}</button>
      </div>
      ${field(t('studio.stop.hold'), num('stop-hold', s.hold ?? NEW_STOP_HOLD, 0.5, 600, 0.5), true)}
      <fieldset class="studio-group">
        <legend>${escapeHtml(t('studio.stop.arrive'))}</legend>
        ${first ? `<p class="studio-note">${escapeHtml(t('studio.stop.firstNote'))}</p>` : `
        ${field(t('studio.move'), `<select class="tr-move">${MOVES.map((m) => option(m, t(`studio.move.${m}`), (tr.move ?? 'fly') === m)).join('')}</select>`)}
        ${field(t('studio.move.seconds'), num('tr-seconds', tr.seconds, 0, 30, 0.5, auto ? `${t('studio.move.auto')} ${auto}` : ''), true)}
        ${field(t('studio.feel'), `<select class="tr-feel">${FEELS.map((f) => option(f, t(`studio.feel.${f}`), (tr.feel ?? 'gentle') === f)).join('')}</select>`)}
        <div class="studio-actions">
          <span class="studio-row-summary tr-path-count">${escapeHtml(t('studio.fx.points', { n: tr.path?.length ?? 0 }))}</span>
          <button type="button" class="tr-path-add">${escapeHtml(t('studio.path.add'))}</button>
          <button type="button" class="tr-path-clear" ${tr.path?.length ? '' : 'disabled'}>${escapeHtml(t('studio.path.clear'))}</button>
        </div>
        ${field(t('studio.dataMove'), `<select class="tr-data">${DATA_MOVES.map((d) => option(d, t(`studio.dataMove.${d}`), (tr.data ?? 'cut') === d)).join('')}</select>`)}
        ${field(t('studio.wipe'), `<select class="tr-wipe" ${tr.data === 'wipe' ? '' : 'disabled'}>${(['meridian', 'terminator'] as const).map((w) => option(w, t(`studio.wipe.${w}`), (tr.wipe ?? 'meridian') === w)).join('')}</select>`)}`}
        ${field(t('studio.during'), `<select class="tr-during">${DURINGS.map((d) => option(d, t(`studio.during.${d}`), (tr.during ?? 'still') === d)).join('')}</select>`)}
        ${field(t('studio.amount'), num('tr-amount', tr.amount, 0, 360, 0.1), true)}
      </fieldset>
      <fieldset class="studio-group">
        <legend>${escapeHtml(t('studio.effects'))}</legend>
        ${check('fx-spot-on', !!fx.spotlight, t('studio.fx.spotlight'))}
        <div class="studio-template-row">
          ${field(t('studio.fx.radius'), num('fx-spot-radius', fx.spotlight?.radius ?? 8, 0.5, 90, 0.5), true)}
          ${field(t('studio.fx.dim'), range('fx-spot-dim', fx.spotlight?.dim ?? 0.55))}
          ${check('fx-spot-grey', fx.spotlight?.desaturate, t('studio.fx.grey'))}
          ${check('fx-spot-edge', fx.spotlight?.outline, t('studio.fx.edge'))}
          <button type="button" class="fx-spot-here" ${fx.spotlight ? '' : 'disabled'}>${escapeHtml(t('studio.fx.spotHere'))}</button>
        </div>
        ${field(t('studio.fx.blur'), range('fx-blur', fx.depthBlur))}
        ${field(t('studio.fx.vignette'), range('fx-vignette', fx.vignette))}
        ${field(t('studio.fx.grain'), range('fx-grain', fx.grain))}
        <div class="studio-template-row">
          ${field(t('studio.fx.card'), `<input type="text" class="fx-card-text" maxlength="120" value="${escapeAttr(textOf(fx.titleCard?.text as Record<string, string> | undefined))}">`)}
          ${field(t('studio.fx.cardSeconds'), num('fx-card-seconds', fx.titleCard?.seconds ?? 2.5, 1, 15, 0.5), true)}
        </div>
        <h5 class="studio-subtitle">${escapeHtml(t('studio.fx.callouts'))}</h5>
        <ol class="studio-list fx-callouts">${callouts}</ol>
        <button type="button" class="fx-callout-add" ${(fx.callouts?.length ?? 0) >= 6 ? 'disabled' : ''}>${escapeHtml(t('studio.fx.calloutAdd'))}</button>
        <h5 class="studio-subtitle">${escapeHtml(t('studio.fx.arrows'))}</h5>
        <ol class="studio-list fx-arrows">${arrows}</ol>
        <button type="button" class="fx-arrow-add" ${(fx.arrows?.length ?? 0) >= 4 ? 'disabled' : ''}>${escapeHtml(t('studio.fx.arrowAdd'))}</button>
        <div class="studio-template-row">
          ${check('fx-compare-on', !!fx.compare, t('studio.fx.compare'))}
          ${field(t('studio.fx.compareAt'), `<input type="date" class="fx-compare-at" value="${escapeAttr(fx.compare?.at?.slice(0, 10) ?? '')}" ${fx.compare ? '' : 'disabled'}>`)}
        </div>
        <div class="studio-template-row">
          ${check('fx-counter-on', !!fx.counter, t('studio.fx.counter'))}
          ${field(t('studio.fx.counterLabel'), `<input type="text" class="fx-counter-label" maxlength="40" value="${escapeAttr(fx.counter?.label ?? '')}" ${fx.counter ? '' : 'disabled'}>`)}
        </div>
        <p class="studio-note">${escapeHtml(t('studio.effects.note'))}</p>
      </fieldset>
    </div>`
}

function render(): void {
  if (!panel || !host) return
  const shot = built()?.shot ?? null
  const film = draft.khipu ? planFilm(steps()) : null
  const myOptions = mine.map((k) => option(k.id, k.title, false)).join('')
  panel.querySelector('.studio-body')!.innerHTML = `
    <label class="studio-field">${escapeHtml(t('studio.shotTitle'))}
      <input type="text" class="studio-title-input" maxlength="120" value="${escapeAttr(draft.title || (draft.khipu ? textOf(draft.khipu.title as Record<string, string>) : host.datasetTitle()))}">
    </label>
    <fieldset class="studio-group">
      <legend>${escapeHtml(t('studio.output'))}</legend>
      <label class="studio-field">${escapeHtml(t('studio.aspect'))}
        <select class="studio-aspect">${SHOT_ASPECTS.map((a) => option(a, t(ASPECT_KEY[a]), a === draft.output.aspect)).join('')}</select>
      </label>
      <label class="studio-field">${escapeHtml(t('studio.resolution'))}
        <select class="studio-resolution">${SHOT_RESOLUTIONS.map((r) => option(r, RESOLUTION_LABEL[r], r === draft.output.resolution)).join('')}</select>
      </label>
      <label class="studio-field">${escapeHtml(t('studio.fps'))}
        <select class="studio-fps">${SHOT_FRAME_RATES.map((f) => option(String(f), String(f), f === draft.output.fps)).join('')}</select>
      </label>
      <label class="studio-field">${escapeHtml(t('studio.format'))}
        <select class="studio-format">${SHOT_FORMATS.map((f) => option(f, t(`studio.format.${f}`), f === draft.output.format)).join('')}</select>
      </label>
      <label class="studio-field studio-field-narrow">${escapeHtml(t('studio.stillAt'))}
        <input type="number" class="studio-still-at" min="0" step="0.5" value="${draft.output.stillAt}">
      </label>
      <label class="studio-check"><input type="checkbox" class="studio-transparent" ${draft.output.transparent ? 'checked' : ''}> ${escapeHtml(t('studio.transparent'))}</label>
      <p class="studio-note">${escapeHtml(t('studio.formatNote'))}</p>
      <p class="studio-length">${escapeHtml(t('studio.stops.length', { seconds: shot ? shot.output.seconds.toFixed(1) : '0' }))}</p>
    </fieldset>
    <fieldset class="studio-group">
      <legend>${escapeHtml(t('studio.stops'))}</legend>
      <div class="studio-actions">
        <button type="button" class="studio-add-stop">${escapeHtml(t('studio.stops.add'))}</button>
      </div>
      <p class="studio-note studio-play-note">${escapeHtml(t('studio.play.note'))}</p>
      <div class="studio-template-row">
        <label class="studio-field">${escapeHtml(t('studio.template'))}
          <select class="studio-template">${option('', t('studio.template.none'), true)}${SHOT_TEMPLATES.map((k) => option(k, t(`studio.template.${k}`), false)).join('')}</select>
        </label>
        <button type="button" class="studio-template-apply">${escapeHtml(t('studio.template.apply'))}</button>
      </div>
      <div class="studio-template-row">
        <label class="studio-field">${escapeHtml(t('studio.stops.open'))}
          <select class="studio-open-khipu">${option('', t('studio.stops.openNone'), true)}${myOptions}</select>
        </label>
        <button type="button" class="studio-open-apply">${escapeHtml(t('studio.stops.openApply'))}</button>
      </div>
      ${timelineHtml(film)}
      ${stopRows()}
      ${stopEditor(film)}
    </fieldset>
    <fieldset class="studio-group">
      <legend>${escapeHtml(t('studio.finish'))}</legend>
      <label class="studio-check"><input type="checkbox" class="fin-blur" ${draft.finish.motionBlur ? 'checked' : ''}> ${escapeHtml(t('studio.finish.motionBlur'))}</label>
      <label class="studio-check"><input type="checkbox" class="fin-endcard" ${draft.finish.endCard ? 'checked' : ''}> ${escapeHtml(t('studio.finish.endCard'))}</label>
      <label class="studio-check"><input type="checkbox" class="fin-brand" ${draft.finish.animatedBrand ? 'checked' : ''}> ${escapeHtml(t('studio.finish.animatedBrand'))}</label>
      <label class="studio-check"><input type="checkbox" class="fin-reframe" ${draft.finish.reframe ? 'checked' : ''}> ${escapeHtml(t('studio.finish.reframe'))}</label>
      <div class="studio-voice">
        <h5 class="studio-subtitle">${escapeHtml(t('studio.voice'))}</h5>
        ${draft.finish.voiceOver ? `
        <p class="studio-row-summary studio-voice-name">${escapeHtml(draft.finish.voiceOver.name)}</p>
        <button type="button" class="studio-voice-remove">${escapeHtml(t('studio.voice.remove'))}</button>` : `
        <label class="studio-check"><input type="checkbox" class="studio-voice-rights"> ${escapeHtml(t('studio.voice.rights'))}</label>
        <label class="studio-field">${escapeHtml(t('studio.voice.file'))}
          <input type="file" class="studio-voice-file" accept="audio/mpeg,audio/mp4,audio/x-m4a,audio/wav,audio/x-wav,audio/ogg,audio/webm,audio/aac,.mp3,.m4a,.wav,.ogg,.webm,.aac" ${voiceBusy ? 'disabled' : ''}>
        </label>`}
        <p class="studio-note">${escapeHtml(t('studio.voice.note'))}</p>
      </div>
    </fieldset>
    <fieldset class="studio-group">
      <legend>${escapeHtml(t('studio.brand'))}</legend>
      <div class="studio-template-row">
        <label class="studio-field">${escapeHtml(t('studio.brand'))}
          <select class="studio-brand">${option('', t('studio.brand.none'), !draft.brand)}${kits.map((k) => option(k.id, k.name, draft.brand?.id === k.id)).join('')}</select>
        </label>
        ${draft.brand ? `<button type="button" class="studio-brand-delete">${escapeHtml(t('studio.brand.delete'))}</button>` : ''}
      </div>
      ${newKitOpen ? `
      <div class="studio-new-kit">
        <label class="studio-field">${escapeHtml(t('studio.brand.name'))}<input type="text" class="studio-kit-name" maxlength="60"></label>
        <label class="studio-field">${escapeHtml(t('studio.brand.logo'))}<input type="file" class="studio-kit-logo" accept="image/png,image/jpeg,image/webp"></label>
        <label class="studio-field">${escapeHtml(t('studio.brand.lowerThird'))}<input type="text" class="studio-kit-lower" maxlength="80"></label>
        <label class="studio-field studio-field-narrow">${escapeHtml(t('studio.brand.accent'))}<input type="color" class="studio-kit-accent" value="${DEFAULT_ACCENT}"></label>
        <button type="button" class="studio-kit-save">${escapeHtml(t('studio.brand.save'))}</button>
      </div>` : `<button type="button" class="studio-brand-new">${escapeHtml(t('studio.brand.new'))}</button>`}
      <p class="studio-note">${escapeHtml(t('studio.brand.note'))}</p>
    </fieldset>
    <div class="studio-actions">
      <button type="button" class="studio-preview" ${shot ? '' : 'disabled'}>${escapeHtml(t(preview ? 'studio.stopPreview' : 'studio.preview'))}</button>
    </div>
    <p class="studio-note">${escapeHtml(t('studio.previewNote'))}</p>
    <p class="studio-price">${escapeHtml(shot ? studioPriceText(shot) : t('studio.stops.empty'))}</p>
    <p class="studio-note">${escapeHtml(t('studio.creditNote'))}</p>
    <div class="studio-actions">
      <button type="button" class="studio-save" ${shot ? '' : 'disabled'}>${escapeHtml(t('studio.save'))}</button>
      ${quote && quote.status === 'quoted' ? `<button type="button" class="studio-confirm">${escapeHtml(t('studio.confirm', { credits: quote.quote }))}</button>` : ''}
    </div>`
  syncFormatFields()
}

/**
 * What follows an edit, set in place (never by redrawing): the length, the
 * timeline's proportions, the stop's hold in the list, and the price; a quote
 * that no longer matches the shot is withdrawn.
 */
function updateDerived(): void {
  if (!panel) return
  quote = null
  panel.querySelector('.studio-confirm')?.remove()
  const b = built()
  const shot = b?.shot ?? null
  const length = panel.querySelector('.studio-length')
  if (length) length.textContent = t('studio.stops.length', { seconds: shot ? shot.output.seconds.toFixed(1) : '0' })
  const price = panel.querySelector('.studio-price')
  if (price) price.textContent = shot ? studioPriceText(shot) : t('studio.stops.empty')
  for (const sel of ['.studio-save', '.studio-preview']) {
    const btn = panel.querySelector<HTMLButtonElement>(sel)
    if (btn) btn.disabled = !shot
  }
  const film = draft.khipu ? planFilm(steps()) : null
  if (film) syncTimeline(film)
  steps().forEach((s, i) => {
    const label = panel!.querySelector(`.studio-stop-row[data-index="${i}"] .studio-stop-hold-label`)
    if (label) label.textContent = `${s.hold ?? NEW_STOP_HOLD} s`
    const name = panel!.querySelector(`.studio-stop-row[data-index="${i}"] .studio-stop-pick`)
    if (name) name.textContent = `${i + 1}. ${stopName(s, i)}`
  })
  syncFormatFields()
}

/**
 * The timeline's proportions, changed in place where its parts are the same:
 * leaving a field edits the draft on the way to a click, and a strip drawn
 * anew under the pointer would swallow that click.
 */
function syncTimeline(film: FilmPlan): void {
  const strip = panel?.querySelector('.studio-timeline')
  if (!strip) return
  const holder = document.createElement('div')
  holder.innerHTML = timelineHtml(film)
  const fresh = holder.firstElementChild
  if (!fresh) return
  const before = [strip, ...strip.querySelectorAll('*')]
  const after = [fresh, ...fresh.querySelectorAll('*')]
  if (before.length !== after.length || before.some((el, i) => el.className !== after[i].className)) return strip.replaceWith(fresh)
  before.forEach((el, i) => el.setAttribute('style', after[i].getAttribute('style') ?? ''))
}

/**
 * The fields a format allows, set in place: a GIF is 1080p, only a still
 * has a moment, only ProRes and PNG can leave space transparent.
 */
function syncFormatFields(): void {
  if (!panel) return
  const f = draft.output.format
  if (f === 'gif') draft.output = { ...draft.output, resolution: '1080p' }
  if (!ALPHA_FORMATS.includes(f)) draft.output = { ...draft.output, transparent: false }
  const field = <T extends HTMLElement>(sel: string) => panel!.querySelector<T>(sel)
  const resolution = field<HTMLSelectElement>('.studio-resolution')
  if (resolution) { resolution.value = draft.output.resolution; resolution.disabled = f === 'gif' }
  const stillAt = field<HTMLInputElement>('.studio-still-at')
  if (stillAt) { stillAt.disabled = f !== 'png'; stillAt.value = String(draft.output.stillAt) }
  const transparent = field<HTMLInputElement>('.studio-transparent')
  if (transparent) { transparent.disabled = !ALPHA_FORMATS.includes(f); transparent.checked = draft.output.transparent }
  // A GIF over 20 s cannot be made: say so where the length is.
  const length = field<HTMLElement>('.studio-length')
  const seconds = draft.khipu ? planFilm(steps())?.seconds ?? 0 : 0
  if (length) length.classList.toggle('is-too-long', f === 'gif' && seconds > GIF_MAX_SECONDS)
}

function readNumber(el: Element | null, fallback: number, lo: number, hi: number): number {
  const raw = (el as HTMLInputElement | null)?.value
  const v = Number(raw)
  return raw !== '' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback
}

/** Change the selected stop (an edit, so nothing redraws). */
function patchStep(patch: Partial<KhipuStep>): void {
  if (!draft.khipu || !step()) return
  draft.khipu = updateStep(draft.khipu, selected, patch)
}
function patchTransition(patch: Partial<KhipuTransition>): void {
  const cur = step()?.transition ?? {}
  const next: KhipuTransition = { ...cur }
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete (next as Record<string, unknown>)[k]
    else (next as Record<string, unknown>)[k] = v
  }
  patchStep({ transition: Object.keys(next).length ? next : undefined })
}
function patchEffects(patch: Partial<KhipuEffects>): void {
  const cur = step()?.effects ?? {}
  const next: KhipuEffects = { ...cur }
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) delete (next as Record<string, unknown>)[k]
    else (next as Record<string, unknown>)[k] = v
  }
  patchStep({ effects: Object.keys(next).length ? next : undefined })
}

/** The centre of the view, rounded as steps keep places. */
function centre(): { lat: number; lon: number } | null {
  const v = host?.view()
  return v ? { lat: Math.round(v.lat * 1e4) / 1e4, lon: Math.round(v.lon * 1e4) / 1e4 } : null
}

function onInput(e: Event): void {
  const target = e.target as HTMLElement
  if (target.closest('.studio-team')) {
    if (e.type === 'change' && target.matches('.studio-team-approval, .studio-team-editor')) void changeTeam(target as HTMLInputElement)
    return
  }
  if (target.matches('.studio-voice-rights')) return
  if (target.matches('.studio-voice-file')) {
    if (e.type === 'change') void uploadVoice(target as HTMLInputElement)
    return
  }
  if (target.matches('.studio-template, .studio-open-khipu') || target.closest('.studio-new-kit')) {
    if (target.matches('.studio-kit-logo') && e.type === 'change') void readLogo(target as HTMLInputElement)
    return
  }
  if (target.matches('.studio-brand')) {
    if (e.type !== 'change') return
    const kit = kits.find((k) => k.id === (target as HTMLSelectElement).value) ?? null
    draft.brand = kit ? { id: kit.id, logo: kit.logo, lowerThird: kit.lowerThird, accent: kit.accent } : null
    quote = null
    return render()
  }
  const value = () => (target as HTMLInputElement).value
  const checked = () => (target as HTMLInputElement).checked
  const s = step()
  if (target.matches('.studio-title-input')) draft.title = value()
  else if (target.matches('.studio-aspect')) draft.output = { ...draft.output, aspect: value() as ShotAspect }
  else if (target.matches('.studio-resolution')) draft.output = { ...draft.output, resolution: value() as ShotResolution }
  else if (target.matches('.studio-fps')) draft.output = { ...draft.output, fps: Number(value()) as ShotOutput['fps'] }
  else if (target.matches('.studio-format')) draft.output = { ...draft.output, format: value() as ShotFormat }
  else if (target.matches('.studio-still-at')) draft.output = { ...draft.output, stillAt: readNumber(target, 0, 0, 600) }
  else if (target.matches('.studio-transparent')) draft.output = { ...draft.output, transparent: checked() }
  else if (target.matches('.fin-blur')) draft.finish = { ...draft.finish, motionBlur: checked() }
  else if (target.matches('.fin-endcard')) draft.finish = { ...draft.finish, endCard: checked() }
  else if (target.matches('.fin-brand')) draft.finish = { ...draft.finish, animatedBrand: checked() }
  else if (target.matches('.fin-reframe')) draft.finish = { ...draft.finish, reframe: checked() }
  else if (!s) return
  else if (target.matches('.stop-title')) patchStep({ title: setText(s.title as Record<string, string> | undefined, lang(), value()) })
  else if (target.matches('.stop-hold')) patchStep({ hold: readNumber(target, s.hold ?? NEW_STOP_HOLD, 0.5, 600) })
  else if (target.matches('.tr-move')) patchTransition({ move: value() as KhipuTransition['move'] })
  else if (target.matches('.tr-seconds')) patchTransition({ seconds: value() === '' ? undefined : readNumber(target, 0, 0, 30) })
  else if (target.matches('.tr-feel')) patchTransition({ feel: value() as KhipuTransition['feel'] })
  else if (target.matches('.tr-during')) patchTransition({ during: value() as KhipuTransition['during'] })
  else if (target.matches('.tr-amount')) patchTransition({ amount: value() === '' ? undefined : readNumber(target, 0, 0, 360) })
  else if (target.matches('.tr-data')) {
    patchTransition({ data: value() as KhipuTransition['data'] })
    const wipe = panel?.querySelector<HTMLSelectElement>('.tr-wipe')
    if (wipe) wipe.disabled = value() !== 'wipe'
  } else if (target.matches('.tr-wipe')) patchTransition({ wipe: value() as KhipuTransition['wipe'] })
  else if (target.matches('.fx-spot-on')) {
    const c = centre()
    patchEffects({ spotlight: checked() && c ? { ...c, radius: 8, dim: 0.55 } : undefined })
    const here = panel?.querySelector<HTMLButtonElement>('.fx-spot-here')
    if (here) here.disabled = !checked()
  } else if (target.matches('.fx-spot-radius, .fx-spot-dim, .fx-spot-grey, .fx-spot-edge')) {
    const spot = s.effects?.spotlight
    if (!spot) return
    patchEffects({ spotlight: {
      ...spot,
      ...(target.matches('.fx-spot-radius') ? { radius: readNumber(target, spot.radius, 0.5, 90) } : {}),
      ...(target.matches('.fx-spot-dim') ? { dim: readNumber(target, 0.55, 0, 1) } : {}),
      ...(target.matches('.fx-spot-grey') ? { desaturate: checked() } : {}),
      ...(target.matches('.fx-spot-edge') ? { outline: checked() } : {}),
    } })
  } else if (target.matches('.fx-blur')) patchEffects({ depthBlur: readNumber(target, 0, 0, 1) || undefined })
  else if (target.matches('.fx-vignette')) patchEffects({ vignette: readNumber(target, 0, 0, 1) || undefined })
  else if (target.matches('.fx-grain')) patchEffects({ grain: readNumber(target, 0, 0, 1) || undefined })
  else if (target.matches('.fx-card-text, .fx-card-seconds')) {
    const text = (panel?.querySelector<HTMLInputElement>('.fx-card-text')?.value ?? '').trim()
    const seconds = readNumber(panel?.querySelector('.fx-card-seconds') ?? null, 2.5, 1, 15)
    patchEffects({ titleCard: text ? { text: setText(s.effects?.titleCard?.text as Record<string, string> | undefined, lang(), text) ?? { [lang()]: text }, seconds } : undefined })
  } else if (target.matches('.fx-callout-label, .fx-callout-value')) {
    const i = Number(target.closest<HTMLElement>('[data-c]')?.dataset.c)
    const list = [...(s.effects?.callouts ?? [])]
    if (!list[i]) return
    list[i] = target.matches('.fx-callout-label') ? { ...list[i], label: value() } : { ...list[i], value: checked() }
    patchEffects({ callouts: list })
  } else if (target.matches('.fx-arrow-label, .fx-arrow-color')) {
    const i = Number(target.closest<HTMLElement>('[data-a]')?.dataset.a)
    const list = [...(s.effects?.arrows ?? [])]
    if (!list[i]) return
    list[i] = target.matches('.fx-arrow-label') ? { ...list[i], label: value() || undefined } : { ...list[i], color: Number(value()) }
    patchEffects({ arrows: list })
  } else if (target.matches('.fx-compare-on')) {
    // The other date starts as the dataset's first: a line sweeping from 30 % to 70 % of the picture.
    const range = host?.dateRange()
    const first = range ? new Date(range.startMs).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)
    patchEffects({ compare: checked() ? { at: `${first}T00:00:00Z`, from: 0.3, to: 0.7 } : undefined })
    const at = panel?.querySelector<HTMLInputElement>('.fx-compare-at')
    if (at) { at.disabled = !checked(); at.value = checked() ? first : '' }
  } else if (target.matches('.fx-compare-at')) {
    const compare = s.effects?.compare
    if (compare && /^\d{4}-\d{2}-\d{2}$/.test(value())) patchEffects({ compare: { ...compare, at: `${value()}T00:00:00Z` } })
  } else if (target.matches('.fx-counter-on')) {
    const c = centre()
    patchEffects({ counter: checked() && c ? { ...c } : undefined })
    const label = panel?.querySelector<HTMLInputElement>('.fx-counter-label')
    if (label) label.disabled = !checked()
  } else if (target.matches('.fx-counter-label')) {
    const counter = s.effects?.counter
    if (counter) patchEffects({ counter: { ...counter, label: value().trim() || undefined } })
  } else return
  updateDerived()
}

/** A new stop from what the globe shows, after the selected one. */
function addStop(): void {
  if (!host) return
  const scene = host.scene()
  if (!scene.view) return
  const at = scene.date ? Date.parse(scene.date) : NaN
  if (isSameStop(step(), { ...scene, playing: false }, Number.isFinite(at) ? at : null)) return say(t('studio.stops.same', { n: selected + 1 }))
  pushStop(scene)
  say(t('studio.stops.added', { n: selected + 1 }))
}

const isoOf = (ms: number) => new Date(ms).toISOString().replace(/.000Z$/, 'Z')

/** Whether stop `s` already shows `scene`'s dataset, place and date. */
function isSameStop(s: KhipuStep | null, scene: SoloScene, atMs: number | null): boolean {
  if (!s || !draft.khipu || !s.view || !scene.view) return false
  const ds = draft.khipu.datasets.find((d) => d.key === s.dataset)
  if ((ds?.appId ?? null) !== scene.datasetId) return false
  const stepAt = s.time?.at ? Date.parse(s.time.at) : null
  if ((stepAt === null) !== (atMs === null) || (stepAt !== null && atMs !== null && Math.abs(stepAt - atMs) >= 1000)) return false
  return isSameView(s.view, scene.view)
}

function isSameView(a: { lat: number; lon: number; zoom: number; bearing?: number; pitch?: number; flat?: boolean }, b: { lat: number; lon: number; zoom: number; bearing?: number; pitch?: number; flat?: boolean }): boolean {
  // AYNI (2026-10-19): the flat map and the globe are different stops.
  if (!!a.flat !== !!b.flat) return false
  return Math.abs(a.lat - b.lat) < 0.01 && Math.abs(a.lon - b.lon) < 0.01 && Math.abs(a.zoom - b.zoom) < 0.02
    && Math.abs((a.bearing ?? 0) - (b.bearing ?? 0)) < 0.5 && Math.abs((a.pitch ?? 0) - (b.pitch ?? 0)) < 0.5
}

/** Put `scene` in as a new stop after the selected one (the first fills the new story's first step), then `patch` it. */
function pushStop(scene: SoloScene, patch: Partial<KhipuStep> = {}): void {
  if (!host) return
  const titles = { titleOf: host.titleOf, layerTitleOf: host.layerTitleOf }
  if (!draft.khipu) {
    // The first stop fills the new story's empty first step.
    const blank = emptyStory(newKhipuId(), { [lang()]: draft.title.trim() || host.datasetTitle() || t('studio.title') }, lang(), Date.now())
    draft.khipu = updateStep(sceneToStep(blank, 0, scene, titles), 0, { hold: NEW_STOP_HOLD, advance: undefined })
    selected = 0
  } else {
    const made = stepFromGlobe(draft.khipu, scene, titles)
    const newStep: KhipuStep = { ...made.step, hold: NEW_STOP_HOLD, transition: { move: 'fly' } }
    delete newStep.advance
    draft.khipu = insertStep(made.khipu, newStep, selected)
    selected = Math.min(selected + 1, steps().length - 1)
  }
  if (Object.keys(patch).length) draft.khipu = updateStep(draft.khipu, selected, patch)
  quote = null
  render()
}

/** While the panel is open: what the data plays becomes stops (`playbackTake.ts`); the preview's own playing does not. */
function watchPlayback(): void {
  if (!host || preview || document.body.classList.contains('render-mode')) {
    playWatch.reset()
    return
  }
  const events = playWatch.feed({ playing: host.playing(), dateMs: host.dateMs(), datasetId: host.datasetId(), nowMs: performance.now() })
  for (const e of events) {
    if (e.kind === 'start') onPlayStart(e)
    else onPlayRun(e)
  }
}

/** The data started playing: a stop on the date it started from, unless the selected stop is already there. */
function onPlayStart(e: Extract<PlaybackEvent, { kind: 'start' }>): void {
  if (!host) return
  const s = step()
  if (runEndStep && s?.id === runEndStep && e.pausedSeconds !== null) {
    // The pause between two plays: that long on the date it stopped on.
    patchStep({ hold: Math.round(Math.min(NEW_STOP_HOLD, Math.max(MIN_HOLD, e.pausedSeconds)) * 10) / 10 })
    updateDerived()
  }
  runEndStep = null
  const scene: SoloScene = { ...host.scene(), date: isoOf(e.fromMs), playing: false }
  if (!scene.view || isSameStop(s, scene, e.fromMs)) return
  pushStop(scene, draft.khipu ? { transition: { move: 'fly' } } : {})
}

/** It played from one date to another: stops that run through the dates for as long as it played, on the place it ended at. */
function onPlayRun(e: Extract<PlaybackEvent, { kind: 'run' }>): void {
  if (!host || !draft.khipu) return
  const pieces = runPieces(e.fromMs, e.toMs, e.seconds)
  pieces.forEach((piece, i) => {
    const scene: SoloScene = { ...host!.scene(), date: isoOf(piece.atMs), playing: false }
    if (!scene.view) return
    const before = step()?.view
    const moved = before ? !isSameView(before, scene.view) : false
    pushStop(scene, {
      hold: i === pieces.length - 1 ? NEW_STOP_HOLD : MIN_HOLD,
      transition: { move: moved ? 'glide' : 'cut', seconds: piece.seconds, data: 'timeRun' },
    })
  })
  runEndStep = step()?.id ?? null
  say(t('studio.play.recorded', { from: host.formatDate(e.fromMs), to: host.formatDate(e.toMs), seconds: e.seconds.toFixed(1), n: selected + 1 }))
}

function applyTemplate(): void {
  const choice = panel?.querySelector<HTMLSelectElement>('.studio-template')?.value as ShotTemplate | ''
  if (!choice || !host) return
  const scene = host.scene()
  if (!scene.view) return
  const titles = { titleOf: host.titleOf, layerTitleOf: host.layerTitleOf }
  const blank = emptyStory(newKhipuId(), { [lang()]: draft.title.trim() || host.datasetTitle() || t('studio.title') }, lang(), Date.now())
  const k = sceneToStep(blank, 0, scene, titles)
  const base = k.path.steps[0]
  if (!base.view) return
  const pins = (scene.pins ?? []).map((p) => ({ lat: p.lat, lon: p.lon, note: (p as { name?: string }).name }))
  const stops = templateStops(choice, { ...base, view: base.view }, host.dateRange(), {
    before: t('studio.template.before'), after: t('studio.template.after'), lang: lang(),
  }, pins)
  if (!stops) return say(t(choice === 'pinJourney' ? 'studio.template.needsPins' : 'studio.template.needsDates'))
  let story: Khipu = { ...k, path: { steps: [] } }
  for (const s of stops) {
    const { advance: _advance, ...rest } = s as KhipuStep
    story = insertStep(story, { ...rest, id: `s${story.path.steps.length + 1}`, n: story.path.steps.length + 1 } as KhipuStep, story.path.steps.length - 1)
  }
  draft.khipu = story
  selected = 0
  quote = null
  render()
  say(t('studio.template.applied'))
}

/** Open one of the account's Khipus as a new video: a copy, so the original (a lesson, a story) stays as it was. */
async function openMine(): Promise<void> {
  const id = panel?.querySelector<HTMLSelectElement>('.studio-open-khipu')?.value
  if (!id) return
  const read = await readKeptKhipu(id)
  if (!read.ok) return say(errorText(read.error, 'studio.error.save'))
  if (!planFilm(read.value.khipu.path.steps)) return say(t('studio.stops.noCamera'))
  loadKhipu(read.value.khipu)
  say(t('studio.stops.opened'))
}

/** Put a Khipu in the Studio as a new video (its steps the stops). */
function loadKhipu(k: Khipu): void {
  draft = { ...draft, khipu: { ...k, id: newKhipuId(), kind: 'story' }, title: textOf(k.title as Record<string, string>) }
  selected = 0
  quote = null
  render()
}

async function refreshMine(): Promise<void> {
  const list = await listKeptKhipus()
  mine = list.ok ? list.value.items.filter((k) => k.kind !== 'research') : []
  const select = panel?.querySelector<HTMLSelectElement>('.studio-open-khipu')
  if (select) select.innerHTML = option('', t('studio.stops.openNone'), true) + mine.map((k) => option(k.id, k.title, false)).join('')
}

/** Read a chosen logo and shrink it to at most 512 px, small enough to travel inside a shot. */
async function readLogo(input: HTMLInputElement): Promise<void> {
  newKitLogo = null
  const file = input.files?.[0]
  if (!file || !/^image\/(png|jpeg|webp)$/.test(file.type)) return
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image()
      i.onload = () => resolve(i)
      i.onerror = reject
      i.src = url
    })
    const scale = Math.min(1, 512 / Math.max(img.width, img.height))
    const canvas = Object.assign(document.createElement('canvas'), { width: Math.round(img.width * scale), height: Math.round(img.height * scale) })
    canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height)
    let data = canvas.toDataURL('image/png')
    if (data.length > BRAND_LOGO_MAX_CHARS) data = canvas.toDataURL('image/webp', 0.85)
    newKitLogo = data.length <= BRAND_LOGO_MAX_CHARS ? data : null
  } catch {
    newKitLogo = null
  } finally {
    URL.revokeObjectURL(url)
  }
}

/**
 * Load the pool's kits. Only the kit picker is refreshed: the list arrives a
 * moment after the panel opens, and a full redraw then would undo whatever
 * the person had just chosen (a template, half a new kit).
 */
async function refreshKits(): Promise<void> {
  const reply = await api('/api/studio/brands')
  kits = reply.ok ? ((reply.body.kits as BrandKitSummary[] | undefined) ?? []) : []
  const select = panel?.querySelector<HTMLSelectElement>('.studio-brand')
  if (!select) return
  select.innerHTML = [{ id: '', name: t('studio.brand.none') }, ...kits]
    .map((k) => `<option value="${escapeAttr(k.id)}" ${(draft.brand?.id ?? '') === k.id ? 'selected' : ''}>${escapeHtml(k.name)}</option>`).join('')
}

async function saveKit(): Promise<void> {
  const form = panel?.querySelector('.studio-new-kit')
  if (!form) return
  const value = (sel: string) => (form.querySelector(sel) as HTMLInputElement | null)?.value ?? ''
  const body = { name: value('.studio-kit-name').trim(), logo: newKitLogo, lowerThird: value('.studio-kit-lower'), accent: value('.studio-kit-accent') || DEFAULT_ACCENT }
  if (!body.name || !checkBrand(body)) return say(t('studio.brand.invalid'))
  const reply = await api('/api/studio/brands', { method: 'POST', body: JSON.stringify(body) })
  if (!reply.ok) return say(t(reply.body.error === 'too_many' ? 'studio.brand.tooMany' : reply.body.error === 'invalid' ? 'studio.brand.invalid' : 'studio.error.generic'))
  const kit = reply.body.kit as BrandKitSummary
  newKitOpen = false
  newKitLogo = null
  draft.brand = { id: kit.id, logo: kit.logo, lowerThird: kit.lowerThird, accent: kit.accent }
  quote = null
  await refreshKits()
  render()
  say(t('studio.brand.saved'))
}

async function deleteKit(): Promise<void> {
  const id = draft.brand?.id
  if (!id) return
  const reply = await api(`/api/studio/brands/${encodeURIComponent(id)}`, { method: 'DELETE' })
  if (!reply.ok) return say(errorText(reply.body.error))
  draft.brand = null
  await refreshKits()
  render()
  say(t('studio.brand.deleted'))
}

function stopPreview(): void {
  preview?.stop()
  preview = null
  voicePlayer?.pause()
}

/** Upload a chosen recording as the video's voice-over, once its rights are confirmed. */
async function uploadVoice(input: HTMLInputElement): Promise<void> {
  const file = input.files?.[0]
  if (!file) return
  if (!panel?.querySelector<HTMLInputElement>('.studio-voice-rights')?.checked) {
    input.value = ''
    return say(t('studio.voice.needRights'))
  }
  if (file.size > 25 * 1024 * 1024) {
    input.value = ''
    return say(t('studio.voice.tooLarge'))
  }
  voiceBusy = true
  input.disabled = true
  say(t('studio.voice.uploading'))
  let reply: { ok: boolean; body: Record<string, unknown> }
  try {
    const r = await fetch('/api/studio/voice', {
      method: 'POST', credentials: 'same-origin', body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Voice-Name': encodeURIComponent(file.name), 'X-Voice-Rights': 'confirmed' },
    })
    reply = { ok: r.ok, body: (await r.json().catch(() => ({}))) as Record<string, unknown> }
  } catch {
    reply = { ok: false, body: {} }
  }
  voiceBusy = false
  if (!reply.ok) {
    input.disabled = false
    input.value = ''
    return say(errorText(reply.body.error))
  }
  const voice = reply.body.voice as { id: string; name: string }
  draft.finish = { ...draft.finish, voiceOver: { id: voice.id, name: voice.name } }
  if (voicePlayer) URL.revokeObjectURL(voicePlayer.src)
  voicePlayer = new Audio(URL.createObjectURL(file))
  quote = null
  render()
  say(t('studio.voice.added', { name: voice.name }))
  panel?.querySelector<HTMLButtonElement>('.studio-voice-remove')?.focus()
}

/** Take the voice-over off the video, and delete the recording. */
async function removeVoice(): Promise<void> {
  const voice = draft.finish.voiceOver
  if (!voice) return
  draft.finish = { ...draft.finish, voiceOver: null }
  voicePlayer?.pause()
  if (voicePlayer) URL.revokeObjectURL(voicePlayer.src)
  voicePlayer = null
  quote = null
  render()
  await api(`/api/studio/voice/${encodeURIComponent(voice.id)}`, { method: 'DELETE' })
  say(t('studio.voice.removed'))
  panel?.querySelector<HTMLInputElement>('.studio-voice-rights')?.focus()
}

function togglePreview(): void {
  if (preview) { stopPreview(); render(); return }
  const b = built()
  if (!b || !host || !b.shot.film) return
  preview = host.previewFilm(b.khipu as unknown as Khipu, b.shot.film, () => { preview = null; voicePlayer?.pause(); render() })
  // The voice-over from the start, as the video will have it (when it was chosen in this session).
  if (voicePlayer && draft.finish.voiceOver) {
    voicePlayer.currentTime = 0
    void voicePlayer.play().catch(() => {})
  }
  render()
}

async function api(path: string, init?: RequestInit): Promise<{ ok: boolean; body: Record<string, unknown> }> {
  try {
    const reply = await fetch(path, { credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, ...init })
    return { ok: reply.ok, body: (await reply.json().catch(() => ({}))) as Record<string, unknown> }
  } catch {
    return { ok: false, body: {} }
  }
}

async function saveAndQuote(): Promise<void> {
  if (!host) return
  const b = built()
  if (!b) return say(t('studio.stops.empty'))
  const saved = await saveKhipu(b.khipu as unknown as Khipu)
  if (!saved.ok) return say(errorText(saved.error, 'studio.error.save'))
  const reply = await api('/api/studio/jobs', { method: 'POST', body: JSON.stringify({ khipuId: saved.value.id }) })
  if (!reply.ok) return say(errorText(reply.body.error))
  quote = reply.body.job as JobSummary
  render()
  say(t('studio.saved'))
  panel?.querySelector<HTMLButtonElement>('.studio-confirm')?.focus()
}

/** A delivered video's poster and embed code, for the outlet's own site. */
function embedBlock(j: JobSummary): string {
  // Older replies may lack the format: those jobs are MP4.
  const output: ShotOutput = { ...j.output, format: j.output.format ?? 'mp4' }
  const code = embedSnippet(j.id, output)
  const poster = ['mp4', 'hevc', 'gif'].includes(output.format)
    ? `<a class="studio-poster" href="/api/studio/jobs/${escapeAttr(j.id)}/poster" download>${escapeHtml(t('studio.poster'))}</a>`
    : ''
  if (!code) return poster
  return `${poster}
        <details class="studio-embed">
          <summary>${escapeHtml(t('studio.embed'))}</summary>
          <textarea class="studio-embed-code" readonly rows="6" aria-label="${escapeAttr(t('studio.embed'))}">${escapeHtml(code)}</textarea>
          <button type="button" class="studio-embed-copy">${escapeHtml(t('studio.copy'))}</button>
          <p class="studio-note">${escapeHtml(t('studio.embedNote'))}</p>
        </details>`
}

async function copyEmbed(button: HTMLElement): Promise<void> {
  const code = button.closest('.studio-embed')?.querySelector<HTMLTextAreaElement>('.studio-embed-code')
  if (!code) return
  try {
    await navigator.clipboard.writeText(code.value)
    say(t('studio.copied'))
  } catch {
    // No clipboard (an insecure page, a refused permission): select it to copy by hand.
    code.select()
  }
}

async function keepVideo(jobId: string): Promise<void> {
  const reply = await api(`/api/studio/jobs/${encodeURIComponent(jobId)}/keep`, { method: 'POST' })
  if (!reply.ok) return say(errorText(reply.body.error))
  say(t('studio.kept'))
  await refreshOrders()
}

async function confirmQuote(): Promise<void> {
  if (!quote) return
  const reply = await api(`/api/studio/jobs/${quote.id}/confirm`, { method: 'POST' })
  if (!reply.ok) return say(errorText(reply.body.error))
  quote = null
  render()
  say(t(reply.body.approval === 'pending' ? 'studio.approvalSent' : 'studio.ordered'))
  void refreshOrders()
}

/** An editor answers a member's order (J12). */
async function decide(jobId: string, action: 'approve' | 'decline'): Promise<void> {
  const reply = await api(`/api/studio/jobs/${encodeURIComponent(jobId)}/${action}`, { method: 'POST' })
  if (!reply.ok) return say(errorText(reply.body.error))
  say(t(action === 'approve' ? 'studio.approved' : 'studio.declined'))
  await refreshOrders()
}

/** The team section: editors get the switch and the members; others a note when approval is on. */
async function refreshTeam(state: TeamSummary | null): Promise<void> {
  const box = panel?.querySelector<HTMLElement>('.studio-team')
  if (!box) return
  if (!state?.team) { box.innerHTML = ''; return }
  let members: TeamSummary['members'] = []
  if (state.editor) {
    const reply = await api('/api/studio/team')
    members = reply.ok ? (reply.body as unknown as TeamSummary).members ?? [] : []
  }
  if (!panel) return
  box.innerHTML = `
    <h3 class="studio-subtitle">${escapeHtml(t('studio.team'))}</h3>
    ${state.editor ? `
      <label class="studio-check"><input type="checkbox" class="studio-team-approval" ${state.approvalRequired ? 'checked' : ''}> ${escapeHtml(t('studio.team.approval'))}</label>
      <ul class="studio-list studio-team-members">${members.map((m) => `
        <li class="studio-order"><span>${escapeHtml(m.email)}</span>
          <label class="studio-check"><input type="checkbox" class="studio-team-editor" data-account="${escapeAttr(m.accountId)}" ${m.editor ? 'checked' : ''}> ${escapeHtml(t('studio.team.editor'))}</label>
        </li>`).join('')}</ul>
      <p class="studio-note">${escapeHtml(t('studio.team.note'))}</p>`
    : state.approvalRequired ? `<p class="studio-note">${escapeHtml(t('studio.team.approvalOn'))}</p>` : `<p class="studio-note">${escapeHtml(t('studio.team.note'))}</p>`}`
}

async function changeTeam(target: HTMLInputElement): Promise<void> {
  const body = target.matches('.studio-team-approval')
    ? { approvalRequired: target.checked }
    : { editor: { accountId: target.dataset.account, on: target.checked } }
  const reply = await api('/api/studio/team', { method: 'PUT', body: JSON.stringify(body) })
  if (!reply.ok) { target.checked = !target.checked; return say(errorText(reply.body.error)) }
  say(t('studio.team.saved'))
  await refreshTeam(reply.body as unknown as TeamSummary)
}

async function refreshOrders(): Promise<void> {
  window.clearTimeout(pollTimer)
  const list = panel?.querySelector<HTMLElement>('.studio-orders')
  const balance = panel?.querySelector<HTMLElement>('.studio-balance')
  if (!list || !balance) return
  const reply = await api('/api/studio/jobs')
  if (!reply.ok || !panel) {
    list.innerHTML = ''
    balance.textContent = reply.body.error ? errorText(reply.body.error) : ''
    return
  }
  balance.textContent = t('studio.balance', { credits: Number(reply.body.balance ?? 0) })
  const approvals = (reply.body.approvals as Record<string, 'pending' | 'approved' | 'declined'> | undefined) ?? {}
  const team = (reply.body.team as TeamSummary | null | undefined) ?? null
  const waiting = (j: JobSummary) => j.status === 'quoted' && approvals[j.id] === 'pending'
  // Quotes stay out of the list, except those waiting for an editor.
  const jobs = ((reply.body.jobs as JobSummary[] | undefined) ?? []).filter((j) => j.status !== 'quoted' || waiting(j)).slice(0, 8)
  void refreshTeam(team)
  const keepUntil = typeof reply.body.keepUntil === 'string' ? Date.parse(reply.body.keepUntil) : NaN
  const keepCost = Number(reply.body.keepCost ?? 1)
  const day = (ms: number) => new Date(ms).toLocaleDateString()
  // A delivered video can be kept until the licence's term ends, unless it already is.
  const keepable = (j: JobSummary) => j.status === 'delivered' && Number.isFinite(keepUntil) && (!j.expiresAt || Date.parse(j.expiresAt) < keepUntil)
  list.innerHTML = jobs.length
    ? jobs.map((j) => `
      <li class="studio-order">
        <span>${escapeHtml(new Date(j.createdAt).toLocaleDateString())} · ${escapeHtml(t(`studio.format.${j.output.format ?? 'mp4'}`))} · ${escapeHtml(RESOLUTION_LABEL[j.output.resolution])}${j.output.format === 'png' ? '' : ` · ${j.output.seconds} s`}</span>
        <span class="studio-order-status">${escapeHtml(waiting(j) ? t('studio.status.awaitingApproval') : t(`studio.status.${j.status}`))}</span>
        ${waiting(j) && team?.editor ? `<button type="button" class="studio-approve" data-job="${escapeAttr(j.id)}">${escapeHtml(t('studio.approve', { credits: j.quote }))}</button><button type="button" class="studio-decline" data-job="${escapeAttr(j.id)}">${escapeHtml(t('studio.decline'))}</button>` : ''}
        ${j.status === 'delivered' ? `<a class="studio-download" href="/api/studio/jobs/${escapeAttr(j.id)}/download" download>${escapeHtml(t('studio.download'))}</a>` : ''}
        ${j.status === 'delivered' && j.expiresAt ? `<span class="studio-order-until">${escapeHtml(t('studio.keptUntil', { date: day(Date.parse(j.expiresAt)) }))}</span>` : ''}
        ${keepable(j) ? `<button type="button" class="studio-keep" data-job="${escapeAttr(j.id)}">${escapeHtml(t('studio.keep', { date: day(keepUntil), credits: keepCost }))}</button>` : ''}
        ${j.status === 'delivered' ? embedBlock(j) : ''}
      </li>`).join('')
    : `<li class="studio-empty">${escapeHtml(t('studio.ordersEmpty'))}</li>`
  if (jobs.some((j) => j.status === 'rendering')) pollTimer = window.setTimeout(() => void refreshOrders(), POLL_MS)
}

/** Choose a stop (the list, the timeline): its form opens. */
function pick(i: number): void {
  if (i < 0 || i >= steps().length) return
  selected = i
  render()
  panel?.querySelector<HTMLElement>(`.studio-stop-row[data-index="${i}"] .studio-stop-pick`)?.focus()
}

function onClick(e: Event): void {
  const target = e.target as HTMLElement
  const row = target.closest<HTMLElement>('.studio-stop-row')
  const index = row ? Number(row.dataset.index) : -1
  if (target.closest('.studio-close')) return closeStudio()
  if (target.matches('.studio-add-stop')) return addStop()
  if (target.matches('.studio-template-apply')) return applyTemplate()
  if (target.matches('.studio-open-apply')) return void openMine()
  if (target.matches('.studio-tl-hold')) return pick(Number(target.dataset.index))
  if (target.matches('.studio-stop-pick')) return pick(index)
  if (target.matches('.studio-stop-up, .studio-stop-down') && draft.khipu) {
    const delta = target.matches('.studio-stop-up') ? -1 : 1
    draft.khipu = moveStep(draft.khipu, index, delta)
    selected = Math.max(0, Math.min(steps().length - 1, index + delta))
    quote = null
    return render()
  }
  if (target.matches('.studio-stop-remove') && draft.khipu) {
    draft.khipu = removeStep(draft.khipu, index)
    selected = Math.min(selected, steps().length - 1)
    quote = null
    return render()
  }
  if (target.matches('.stop-show') && draft.khipu) return void host?.showStep(draft.khipu, selected)
  if (target.matches('.stop-use') && draft.khipu && host) {
    draft.khipu = sceneToStep(draft.khipu, selected, host.scene(), { titleOf: host.titleOf, layerTitleOf: host.layerTitleOf })
    quote = null
    render()
    return say(t('studio.stop.used'))
  }
  const s = step()
  const c = centre()
  if (s && c) {
    if (target.matches('.tr-path-add')) { patchTransition({ path: [...(s.transition?.path ?? []), c], move: 'path' }); return render() }
    if (target.matches('.tr-path-clear')) { patchTransition({ path: undefined, move: s.transition?.move === 'path' ? 'fly' : s.transition?.move }); return render() }
    if (target.matches('.fx-spot-here') && s.effects?.spotlight) { patchEffects({ spotlight: { ...s.effects.spotlight, ...c } }); updateDerived(); return say(t('studio.fx.moved')) }
    if (target.matches('.fx-callout-add')) {
      patchEffects({ callouts: [...(s.effects?.callouts ?? []), { ...c, label: '', value: true }] })
      render()
      return panel?.querySelector<HTMLInputElement>('.fx-callouts .studio-row:last-child .fx-callout-label')?.focus()
    }
    if (target.matches('.fx-arrow-add')) { patchEffects({ arrows: [...(s.effects?.arrows ?? []), { points: [c, c] }] }); render(); return say(t('studio.fx.arrowStarted')) }
    if (target.matches('.fx-arrow-point')) {
      const i = Number(target.closest<HTMLElement>('[data-a]')?.dataset.a)
      const list = [...(s.effects?.arrows ?? [])]
      if (!list[i]) return
      // An arrow starts with its first point twice; the first added point replaces the copy.
      const pts = list[i].points.length === 2 && list[i].points[0].lat === list[i].points[1].lat && list[i].points[0].lon === list[i].points[1].lon
        ? [list[i].points[0], c] : [...list[i].points, c].slice(0, 50)
      list[i] = { ...list[i], points: pts }
      patchEffects({ arrows: list })
      return render()
    }
  }
  if (s && target.matches('.fx-callout-remove')) {
    const i = Number(target.closest<HTMLElement>('[data-c]')?.dataset.c)
    patchEffects({ callouts: (s.effects?.callouts ?? []).filter((_, j) => j !== i) })
    return render()
  }
  if (s && target.matches('.fx-arrow-remove')) {
    const i = Number(target.closest<HTMLElement>('[data-a]')?.dataset.a)
    patchEffects({ arrows: (s.effects?.arrows ?? []).filter((_, j) => j !== i) })
    return render()
  }
  if (target.matches('.studio-brand-new')) { newKitOpen = true; render(); panel?.querySelector<HTMLInputElement>('.studio-kit-name')?.focus(); return }
  if (target.matches('.studio-kit-save')) return void saveKit()
  if (target.matches('.studio-brand-delete')) return void deleteKit()
  if (target.matches('.studio-preview')) return togglePreview()
  if (target.matches('.studio-voice-remove')) return void removeVoice()
  if (target.matches('.studio-save')) return void saveAndQuote()
  if (target.matches('.studio-confirm')) return void confirmQuote()
  if (target.matches('.studio-keep') && target.dataset.job) return void keepVideo(target.dataset.job)
  if (target.matches('.studio-embed-copy')) return void copyEmbed(target)
  if (target.matches('.studio-approve') && target.dataset.job) return void decide(target.dataset.job, 'approve')
  if (target.matches('.studio-decline') && target.dataset.job) return void decide(target.dataset.job, 'decline')
}

/** Drag a hold's end on the timeline to change how long the stop holds. */
function onPointerDown(e: PointerEvent): void {
  const handle = (e.target as HTMLElement).closest<HTMLElement>('.studio-tl-handle')
  const strip = handle?.closest<HTMLElement>('.studio-timeline')
  if (!handle || !strip || !draft.khipu) return
  const i = Number(handle.dataset.index)
  const s = steps()[i]
  const film = planFilm(steps())
  if (!s || !film) return
  e.preventDefault()
  // Each change redraws the strip (and this handle), so the drag listens on the window.
  const pxPerSecond = strip.getBoundingClientRect().width / film.seconds
  const startX = e.clientX
  const startHold = s.hold ?? NEW_STOP_HOLD
  const move = (ev: PointerEvent) => {
    const hold = Math.max(0.5, Math.min(600, Math.round((startHold + (ev.clientX - startX) / pxPerSecond) * 2) / 2))
    if (!draft.khipu || hold === (steps()[i]?.hold ?? NEW_STOP_HOLD)) return
    draft.khipu = updateStep(draft.khipu, i, { hold })
    updateDerived()
    if (i === selected) { const input = panel?.querySelector<HTMLInputElement>('.stop-hold'); if (input) input.value = String(hold) }
  }
  const up = () => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', up)
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', up)
}

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape' && panel) {
    e.stopPropagation()
    closeStudio()
  }
}

/** The side panels' event (analyzeUI.ts `SIDE_PANEL_EVENT`): the globe and the corner buttons step aside (globePanelOffset.ts). */
const SIDE_PANEL_EVENT = 'ayni-side-panel'
const announceSidePanel = (): void => { window.dispatchEvent(new Event(SIDE_PANEL_EVENT)) }

/** The panel's foot clears the transport and the Tools bar, like Analyze's (`positionAnalyzePanel`). */
function positionPanel(): void {
  if (!panel) return
  let clearance = 0
  for (const id of ['playback-controls', 'map-controls']) {
    const el = document.getElementById(id)
    if (!el || el.classList.contains('hidden')) continue
    const rect = el.getBoundingClientRect()
    if (rect.height > 0) clearance = Math.max(clearance, window.innerHeight - rect.top)
  }
  panel.style.insetBlockEnd = clearance > 0 && window.innerWidth > 600 ? `${clearance + 8}px` : ''
}

export function openStudio(studioHost: StudioHost, from: HTMLElement | null = null): void {
  closeStudio(false)
  host = studioHost
  trigger = from
  panel = document.createElement('section')
  panel.id = PANEL_ID
  panel.className = 'studio-panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-labelledby', 'studio-title')
  panel.innerHTML = `
    <header class="studio-header">
      <h2 class="studio-title" id="studio-title">${escapeHtml(t('studio.title'))}</h2>
      <button type="button" class="studio-close" aria-label="${escapeAttr(t('studio.close'))}">✕</button>
    </header>
    <p class="studio-intro">${escapeHtml(t('studio.intro'))}</p>
    <div class="studio-body"></div>
    <p class="studio-status" role="status" aria-live="polite"></p>
    <h3 class="studio-subtitle">${escapeHtml(t('studio.orders'))}</h3>
    <p class="studio-balance"></p>
    <ul class="studio-list studio-orders"></ul>
    <div class="studio-team"></div>`
  document.body.appendChild(panel)
  panel.addEventListener('click', onClick)
  panel.addEventListener('input', onInput)
  panel.addEventListener('change', onInput)
  panel.addEventListener('pointerdown', onPointerDown)
  window.addEventListener('keydown', onKey, true)
  window.addEventListener('resize', positionPanel)
  render()
  positionPanel()
  announceSidePanel()
  playWatch.reset()
  runEndStep = null
  playWatchTimer = window.setInterval(watchPlayback, PLAY_WATCH_MS)
  void refreshOrders()
  void refreshKits()
  void refreshMine()
  panel.querySelector<HTMLElement>('input, select, button')?.focus({ preventScroll: true })
}

/** "Make a video of this Khipu": open the Studio with a copy of it, its steps the stops. */
export function filmKhipu(studioHost: StudioHost, k: Khipu, from: HTMLElement | null = null): void {
  openStudio(studioHost, from)
  loadKhipu(k)
}

/** Close the Studio: it slides out (at once without motion, or when it is being replaced). */
export function closeStudio(animate = true): void {
  if (!panel) return
  stopPreview()
  window.clearTimeout(pollTimer)
  window.clearInterval(playWatchTimer)
  window.removeEventListener('keydown', onKey, true)
  window.removeEventListener('resize', positionPanel)
  const el = panel
  const still = typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!animate || still) el.remove()
  else {
    // Out of the way at once (no longer #studio-panel), gone when it has slid out.
    el.removeAttribute('id')
    el.classList.add('studio-leaving')
    el.setAttribute('aria-hidden', 'true')
    el.inert = true
    window.setTimeout(() => el.remove(), 700)
  }
  panel = null
  announceSidePanel()
  host = null
  trigger?.focus()
  trigger = null
}

/** Tests only: start from an empty draft. */
export function resetStudioDraftForTests(): void {
  draft = freshDraft()
  selected = 0
  quote = null
  kits = []
  mine = []
  newKitOpen = false
}
